import React, { useEffect, useState } from "react"
import Button from "../../components/button"

// screens
import CardCreate from "../../pages/cards/create"

// redux
import { mapDispatchProps, mapStateToProps } from "../../store/store"
import { connect } from "react-redux"

import clsx from "clsx"
import API from "../../api"
import Card from "../../components/card"
import useInfiniteScroll from "../../hooks/useInfiniteScroll"
import { useLanguage } from "@languages/index"
import { Elements as StripeElements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { Elements as MulpayElements } from "@mul-pay/mptoken-react-js"

const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY)

const CardSelect = (props: any) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.cards }

  const METHODS = {
    SELECT: 1,
    CREATE: 2,
  }

  const [mulpayPromise, setMulpayPromise] = useState<Promise<any> | null>(null);

  const [currentMethod, setCurrentMethod] = useState(METHODS.SELECT)
  const [cards, setCards] = useState([])
  const [isFetchFinished, setIsFetchFinished] = useState(false)
  const [isContentEnded, setIsContentEnded] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isFetching, setIsFetching] = useInfiniteScroll(() => {
    if (cards.length) {
      fetchCards()
    }
  })

  useEffect(() => {
    props.setBrand({
      ...props.brand,
      ...{ cards: cards },
    })
  }, [cards])
  
  // MOUNT MULPAY
  useEffect(() => {
    let isMounted = true;
 
    const loadMulpayModule = async () => {
      try {
        const { loadMulpay } = await import('@mul-pay/mptoken-js');
        if (isMounted) {
          const apiKey = process.env.GMO_API_KEY
          const publicKey = process.env.GMO_PUBLIC_KEY
          const merchantIds = {
            // googlePayMerchantId: "01234567890123456789"
          }
          const promise = loadMulpay(apiKey, publicKey, undefined, true, true);
          setMulpayPromise(promise);
        }
      } catch (error) {
        console.error('Failed to load mulpay module', error);
      }
    };
 
    loadMulpayModule();
 
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchCards = (refresh?: Boolean) => {
    if (isContentEnded && !refresh) {
      return false
    }

    const targetPage = refresh ? 1 : currentPage

    API.Card.index({
      ...{
        brandToken: props.auth.token,
        page: targetPage,
      },
    })
      .then((response) => {
        const fetchedCards = response.data.cards.data
        setCards(targetPage === 1 ? fetchedCards : cards.concat(fetchedCards))

        setCurrentPage(targetPage + 1)
        setIsFetchFinished(true)

        if (response.data.cards.currentPage == response.data.cards.lastPage) {
          setIsContentEnded(true)
        }

        setIsFetching(false)
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
  }

  const renderSelectScreen = () => {
    const classNameHasPadding =
      props.className &&
      (props.className.includes("p-") || props.className.includes("px-") || props.className.includes("py-"))

    return (
      <div
        className={clsx(
          "flex flex-col bg-white overflow-scroll w-full md:max-w-[800px] md:min-w-[370px] mx-auto",
          classNameHasPadding ? props.className : "p-[20px] border border-solid border-[#E8E8E8]"
        )}
      >
        {props.hidePageTitle ? null : (
          <div className="flex flex-col">
            <div className="flex flex-row mb-2 justify-between">
              <div className="flex flex-col">
                <span className="font-bold text-lg tracking-tighter text-[#121212]">{languageTexts.payment_info}</span>
              </div>
              <div className="flex flex-col"></div>
            </div>
          </div>
        )}

        <div className={clsx(props.cardsContainerClassName ? props.cardsContainerClassName : "py-3")}>
          {renderCards()}
          {renderCreateCardButton()}
          {/* {renderSwitchCardButton()} */}
        </div>
      </div>
    )
  }

  const renderCreateScreen = () => {
    return (
      <div className="flex flex-col p-[20px] lg:p-[20px] bg-white overflow-scroll pb-[100px] md:pb-0 w-full mx-auto border">
        {props.hidePageTitle ? null : (
          <div className="flex flex-col">
            <div className="flex flex-row mb-2">
              <span className="font-bold text-xl tracking-tighter text-[#121212]">{languageTexts.index_text_2}</span>
            </div>
          </div>
        )}

        {(mulpayPromise != null && stripePromise != null) && (
          <MulpayElements multiPayment={mulpayPromise}>
            <StripeElements stripe={stripePromise}>
              <CardCreate
                cardsContainerClassName={"p-0"}
                onCardCreated={() => {
                  setCurrentMethod(METHODS.SELECT)
                  fetchCards(true)
                }}
                onClose={() => {
                  setCurrentMethod(METHODS.SELECT)
                  fetchCards(true)
                }}
              />
            </StripeElements>
          </MulpayElements>
        )}
      </div>
    )
  }

  const renderPage = () => {
    if (currentMethod == METHODS.SELECT) return renderSelectScreen()
    if (currentMethod == METHODS.CREATE) return renderCreateScreen()
  }

  const renderCards = () => {
    if (isFetchFinished && cards.length == 0) {
      return (
        <div className="py-10 flex flex-col justify-center w-full items-center">
          <div className="mx-5 flex">
            <span className="tracking-tighter text-base opacity-50">{languageTexts.index_text_1}</span>
          </div>
        </div>
      )
    }

    return (
      <div className="justify-center mx-auto items-center max-w-sm min-w-sm">
        <div className="w-full flex-col">
          {cards.map((card) => {
            return (
              <Card
                key={`Card${card.id}`}
                card={card}
                onCardUpdated={() => {
                  fetchCards(true)
                }}
              />
            )
          })}

          {isFetchFinished && cards.length > 0 ? (
            <div className="flex flex-col justify-center items-center w-full border border-solid border-[#E8E8E8] py-[15px] mt-[10px]">
              <span
                className="text-[15px] text-black font-bold tracking-tighter cursor-pointer"
                onClick={() => {
                  setCurrentMethod(METHODS.CREATE)
                }}
              >
                {languageTexts.payment_card_update}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  const renderCreateCardButton = () => {
    if (cards.length) {
      return <></>
    }

    return (
      <div className={clsx([
        "flex flex-col justify-center items-center",
        props.cardButtonContainerSpaceClassName ?? "mb-5 pb-5"
      ])}>
        <div className="mx-5 mt-5 flex w-full max-w-sm min-w-sm">
          <Button
            text={languageTexts.create_text_11}
            textColor="WHITE"
            buttonColor={"BLACK"}
            borderColor={"BLACK"}
            onClick={() => {
              setCurrentMethod(METHODS.CREATE)
            }}
          />
        </div>
      </div>
    )
  }

  // const renderSwitchCardButton = () => {
  //   if (!cards.length) {
  //     return <></>
  //   }

  //   return (
  //     <div className="flex flex-col justify-center items-center mb-5 pb-5">
  //       <div className="mx-5 mt-5 flex w-full max-w-sm min-w-sm">
  //         <Button
  //           textClassName="text-base tracking-tighter text-[#121212]"
  //           text={languageTexts.index_text_3}
  //           onClick={() => {
  //             setCurrentMethod(METHODS.CREATE)
  //           }}
  //         />
  //       </div>
  //     </div>
  //   )
  // }

  useEffect(() => {
    if (!cards.length && !isContentEnded) {
      fetchCards(true)
    }
  }, [cards])

  useEffect(() => {
    switch (currentMethod) {
      case METHODS.CREATE:
        break
      case METHODS.SELECT:
        break
    }
  }, [currentMethod])

  return <>{renderPage()}</>
}

export default connect(mapStateToProps, mapDispatchProps)(CardSelect)
